import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * Which store's push transport a token belongs to.
 *
 * FCM serves both, so this is not a routing decision — it is what lets an
 * operator read "every Android delivery is failing" out of the log, and what a
 * future APNs-direct adapter would branch on.
 */
export const DEVICE_PLATFORMS = ['ios', 'android'] as const;

export const DevicePlatformSchema = z.enum(DEVICE_PLATFORMS);

export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

/**
 * An FCM registration token is opaque and long — currently ~163 characters, and
 * Google has changed that before. The bound exists so a client bug cannot write
 * a megabyte into the column, not to describe the format.
 */
export const DEVICE_TOKEN_MAX_LENGTH = 512;

export const DeviceTokenValueSchema = z.string().trim().min(16).max(DEVICE_TOKEN_MAX_LENGTH);

/**
 * A registered device, as the member's own account surface could list it.
 *
 * The token itself is **not** on this shape. It is a bearer credential for
 * sending to that device: anything holding it can push to the member's lock
 * screen, so it is written by the client and never read back — the same
 * discipline N21 keeps for vault secrets and N2 for `passwordHash`.
 */
export const DeviceTokenSchema = z
  .object({
    id: UuidSchema,
    platform: DevicePlatformSchema,
    lastSeenAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
  })
  .strict();

export type DeviceToken = z.infer<typeof DeviceTokenSchema>;
